import {sleep} from '@benchling/js-utils';
import moment from 'moment-timezone';

import * as net from 'util/Net';

import {
  InstrumentData,
  InstrumentQuery,
  InstrumentQueryStatus,
  ListTablesParams,
} from './InstrumentDataConstants';

const ITERATION_WAIT_TIME = 5 * 1000; // 5 seconds
const MAX_WAIT_TIME = 5 * 60 * 1000; // 5 minutes
const MAX_RETRY_ATTEMPTS = 100;

export function searchInstrumentData(data: {
  searchFilter: string;
  connectionFilter: Array<string>;
  nextToken: string;
  fileTypeFilter: string;
  instrumentQueryFilter: Array<string>;
  transformDataTypeFilter: string | null;
}): Promise<{instrumentData: Array<InstrumentData>; nextToken: string}> {
  return net.postJSONPromise({url: '/1/api/instruments/instrument-data', data});
}

// TODO generalize beyond Chromeleon
export function createBrowseFilesInstrumentQuery(
  connectionId: string,
  username?: string
): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId,
      command: 'BROWSE_FILES',
      // the instruments service only needs the username and automatically replaces this when sending the actual query
      params: username ? {username, password: '******'} : {},
    },
  });
}

export function createCheckStatusInstrumentQuery(connectionId: string): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId,
      command: 'CHECK_STATUS',
      params: {},
    },
  });
}

// Validates a user's credentials against the connection's instrument software by logging in,
// without performing any other action. Used to gate per-user-login connections (e.g. Empower)
// before running COPY_OBJECT. Storing the entered credentials as a per-user secret is handled
// service-side while processing this query.
export function createValidateCredentialsInstrumentQuery(
  connectionId: string,
  credentials: {username: string; password: string}
): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId,
      command: 'VALIDATE_CREDENTIALS',
      params: credentials,
    },
  });
}

// Requests one page of the connection's Snowflake tables: up to `limit` tables starting
// `offset` tables in, keeping only tables whose name contains `search` (case-insensitive).
export function createListTablesInstrumentQuery(
  connectionId: string,
  params: ListTablesParams
): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId,
      command: 'LIST_TABLES',
      params,
    },
  });
}

/**
 * The run whose fields an instrument query sends to the connection, by its warehouse id (`runId`) or its
 * API id (`runApiId`).
 */
export type RunReference = {runId: string} | {runApiId: string};

export function createCopyObjectInstrumentQuery(
  instrumentData: InstrumentData,
  params: {[key: string]: string | boolean} | null,
  run?: RunReference
): Promise<{instrumentQueryId: string}> {
  if (!params) {
    params = {};
  }
  params.file_id = instrumentData.id;
  if (params?.username) {
    // the instruments service only needs the username and automatically replaces this when sending the actual query
    params.password = '******';
  }
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId: instrumentData.connectionId,
      command: 'COPY_OBJECT',
      ...run,
      params,
      // IANA zone name (DST-correct per date). The backend localizes bare-date PI window
      // bounds to midnight in this zone so the adapter receives an offset-bearing timestamp.
      browserTimezone: moment.tz.guess(),
    },
  });
}

export function createCopyObjectInstrumentQueryFromRun(
  connectionId: string,
  run: RunReference,
  params?: {[key: string]: string}
): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId,
      command: 'COPY_OBJECT',
      ...run,
      params: params ?? {},
      // IANA zone name (DST-correct per date). The backend localizes bare-date PI window
      // bounds to midnight in this zone so the adapter receives an offset-bearing timestamp.
      browserTimezone: moment.tz.guess(),
    },
  });
}

export function createSendInputFileInstrumentQuery(
  automationInputFileId: string,
  connectionId: string,
  username: string | null
): Promise<{instrumentQueryId: string}> {
  const paramsDict =
    username == null
      ? {}
      : {
          username,
          // the instruments service only needs the username and automatically replaces this when sending the actual query
          password: '******',
        };
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      automationInputFileId,
      connectionId,
      command: 'SEND_INPUT_FILE',
      params: paramsDict,
    },
  });
}

export function getInstrumentQuery(instrumentQueryId: string): Promise<InstrumentQuery> {
  return net.getJSONPromise(`/1/api/instruments/instrument-queries/${instrumentQueryId}`);
}

export async function pollUntil<T>(
  runTask: () => Promise<T>,
  isComplete: (response: T) => boolean,
  iterationWaitTime: number = ITERATION_WAIT_TIME,
  maxWaitTime: number = MAX_WAIT_TIME,
  maxRetryAttempts: number = MAX_RETRY_ATTEMPTS
): Promise<T> {
  let totalRetries = 0;
  let totalWaitTime = 0;

  while (totalRetries < maxRetryAttempts && totalWaitTime < maxWaitTime) {
    const response = await runTask();
    if (isComplete(response)) {
      return response;
    }

    totalRetries += 1;
    totalWaitTime += iterationWaitTime;
    await sleep(iterationWaitTime);
  }

  if (totalRetries >= maxRetryAttempts) {
    throw new Error(`Polling failed after ${totalRetries} retries. Please try again later.`);
  }

  throw new Error(`Polling failed after ${totalWaitTime} milliseconds. Please try again later.`);
}

export async function pollInstrumentQueryStatus(instrumentQueryId: string): Promise<InstrumentQuery> {
  return await pollUntil<InstrumentQuery>(
    () => getInstrumentQuery(instrumentQueryId),
    (response: InstrumentQuery) =>
      response.status === InstrumentQueryStatus.DONE || response.status === InstrumentQueryStatus.ERROR
  );
}
